import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";

import {
  APIError,
  approveExtensionSubmission,
  cancelExtensionDevelopmentAttempt,
  createExtensionPublisher,
  deactivateExtensionPublisher,
  extensionSubmissionArtifactURL,
  getExtensionPublishers,
  getExtensionSubmissions,
  getExtensionDevelopmentAttempts,
  purgeExtensionDevelopmentDevice,
  getExtensionDevelopmentOutput,
  getExtensionDevelopmentAccesses,
  getExtensionDevelopmentDeviceCandidates,
  deleteExtensionDevelopmentRevision,
  resetExtensionDevelopmentSandboxCredentials,
  restartExtensionDevelopmentAttempt,
  setExtensionDevelopmentDevice,
  requestExtensionDevelopmentRelease,
  rejectExtensionSubmission,
  signExtensionSubmission,
  deleteExtensionSubmission,
  type ExtensionPublisher,
  type ExtensionReviewDecision,
  type ExtensionSubmission,
  type ExtensionDevelopmentAttempt,
  type ExtensionDevelopmentAccess,
  type ExtensionDevelopmentDeviceCandidate,
  type ExtensionDevelopmentSandboxCredentials,
} from "./api";
import { displayLocale } from "./localization";

type DevelopmentLogEntry = { cursor: number; stream: "stdout" | "stderr"; text: string };
type DevelopmentTransferStats = { total_files: number; total_bytes: number; reused_files: number; reused_bytes: number; transferred_files: number; transferred_bytes: number };

const initialReview: ExtensionReviewDecision = {
  review_notes: "",
  release_approved: false,
};

const stateLabels: Record<ExtensionSubmission["state"], string> = {
  pending: "Prüfung offen",
  approved: "Veröffentlichung ausständig",
  published: "Veröffentlicht",
  rejected: "Abgelehnt",
};

const developmentStateLabels: Record<ExtensionDevelopmentAttempt["state"], string> = {
  uploading: "Auf Server gespeichert",
  queued: "Wartet auf Test-Pi",
  downloading: "Wird heruntergeladen",
  preparing: "Build wird vorbereitet",
  building: "Wird gebaut",
  installing: "Wird installiert",
  running: "Läuft",
  succeeded: "Erfolgreich",
  failed: "Fehlgeschlagen",
  release_uploading: "Release wird übertragen",
  release_ready: "Release bereit",
  cancelling: "Wird abgebrochen",
};

const cancellableDevelopmentStates = new Set<ExtensionDevelopmentAttempt["state"]>([
  "queued", "downloading", "preparing", "building", "installing", "running", "release_uploading",
]);

function developmentStatusLabel(attempt: ExtensionDevelopmentAttempt): string {
  if (attempt.state === "running" && attempt.step === "startup_verified") return "Erfolgreich gestartet · läuft";
  if (attempt.state === "release_uploading") {
    const transfer = developmentReleaseTransferStats(attempt.analysis);
    if (!transfer) return `${developmentStateLabels.release_uploading} (0%)`;
    if (transfer.total_bytes > 0) {
      const presentBytes = Math.min(transfer.total_bytes, transfer.reused_bytes + transfer.transferred_bytes);
      return `${developmentStateLabels.release_uploading} (${Math.floor((presentBytes * 100) / transfer.total_bytes)}%)`;
    }
    return `${developmentStateLabels.release_uploading} (100%)`;
  }
  return developmentStateLabels[attempt.state];
}

function developmentStepLabel(step: string): string {
  if (step === "server_upload") return "Server-Upload";
  if (step === "config_rejected") return "Konfiguration abgelehnt";
  if (step === "release-upload") return "Release-Upload";
  if (step === "release-uploaded") return "Release übertragen";
  if (step === "startup_verified") return "Start bestätigt";
  if (step === "completed") return "Programm beendet";
  if (step === "restart_requested") return "Neustart angefordert";
  if (step === "rebuild_requested") return "Neue Einrichtung angefordert";
  return step || "wartet";
}

// A restart rearms this very attempt on the Pi. It is therefore offered for a
// finished run and for a startup-verified one, but never while a build,
// installation or release upload is still active; that must be cancelled
// visibly. A release-bound attempt stays the immutable evidence of its
// submission.
function canRestartDevelopmentAttempt(attempt: ExtensionDevelopmentAttempt): boolean {
  if (attempt.submission_id) return false;
  if (attempt.state === "succeeded" || attempt.state === "failed") return true;
  return attempt.state === "running" && attempt.step === "startup_verified";
}

function canTakeDevelopmentRelease(attempt: ExtensionDevelopmentAttempt): boolean {
  if (!attempt.startup_verified_at || attempt.submission_id) return false;
  if (attempt.action === "build") return attempt.state === "running" || attempt.state === "succeeded";
  return attempt.action === "release" && attempt.state === "failed" && attempt.error_code === "runner_failed";
}

function developmentCandidateReason(candidate: ExtensionDevelopmentDeviceCandidate): string {
  const labels: Record<ExtensionDevelopmentDeviceCandidate["blockers"][number], string> = {
    inactive: "außer Betrieb",
    agent_too_old: candidate.agent_version ? `bestätigt Agent ${candidate.agent_version}, mindestens 0.25.0 nötig` : "noch kein Agent-Heartbeat",
    customer_assignment: "aktuell einem Kunden zugeordnet",
    customer_measurements: "Kundenmessdaten vorhanden",
    cleanup_pending: "Pi-Bereinigung ausstehend – Gerät online lassen",
  };
  return candidate.blockers.map((blocker) => labels[blocker]).join("; ");
}

function developmentDeleteBlocker(attempt: ExtensionDevelopmentAttempt): string {
  if (attempt.delete_blocker === "active") return "Der Versuch läuft noch oder wartet auf den Test-Pi.";
  if (attempt.delete_blocker === "current") return "Diese Revision ist der aktuelle erfolgreiche Stand auf dem Test-Pi.";
  if (attempt.delete_blocker === "release_bound") return "Diese Revision ist bereits an eine Release-Freigabe gebunden.";
  return "Diese Revision kann derzeit nicht gelöscht werden.";
}

function decodeDevelopmentLogBytes(value: string): Uint8Array | null {
  try {
    const binary = window.atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}

function developmentTransferStats(analysis?: Record<string, unknown>): DevelopmentTransferStats | null {
  return parseDevelopmentTransferStats(analysis?.transfer);
}

function developmentReleaseTransferStats(analysis?: Record<string, unknown>): DevelopmentTransferStats | null {
  return parseDevelopmentTransferStats(analysis?.release_transfer);
}

function parseDevelopmentTransferStats(transfer: unknown): DevelopmentTransferStats | null {
  if (!transfer || typeof transfer !== "object") return null;
  const value = transfer as Record<string, unknown>;
  const keys = ["total_files", "total_bytes", "reused_files", "reused_bytes", "transferred_files", "transferred_bytes"] as const;
  if (!keys.every((key) => typeof value[key] === "number" && Number.isFinite(value[key]) && (value[key] as number) >= 0)) return null;
  return Object.fromEntries(keys.map((key) => [key, value[key]])) as DevelopmentTransferStats;
}

function readableSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024).toLocaleString(displayLocale)} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString(displayLocale, { maximumFractionDigits: 1 })} MB`;
}

export function ExtensionAdministration({
  onUnauthorized,
  onError,
  onNotice,
}: {
  onUnauthorized: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [submissions, setSubmissions] = useState<ExtensionSubmission[]>([]);
  const [publishers, setPublishers] = useState<ExtensionPublisher[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [selectedID, setSelectedID] = useState("");
  const [visibleCount, setVisibleCount] = useState(5);
  const [review, setReview] = useState<ExtensionReviewDecision>(initialReview);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showPublisherForm, setShowPublisherForm] = useState(false);
  const [publisherForm, setPublisherForm] = useState({ name: "", signing_public_key: "" });
  const [issuedToken, setIssuedToken] = useState("");
  const [developmentAccesses, setDevelopmentAccesses] = useState<ExtensionDevelopmentAccess[]>([]);
  const [sandboxCredentials, setSandboxCredentials] = useState<ExtensionDevelopmentSandboxCredentials | null>(null);
  const [developmentAttempts, setDevelopmentAttempts] = useState<ExtensionDevelopmentAttempt[]>([]);
  const [selectedDevelopmentAttemptID, setSelectedDevelopmentAttemptID] = useState("");
  const [selectedDevelopmentRevisionID, setSelectedDevelopmentRevisionID] = useState("");
  const [developmentLog, setDevelopmentLog] = useState<DevelopmentLogEntry[]>([]);
  const [developmentLogLoading, setDevelopmentLogLoading] = useState(false);
  const [developmentLogError, setDevelopmentLogError] = useState("");
  const [developmentCandidates, setDevelopmentCandidates] = useState<ExtensionDevelopmentDeviceCandidate[]>([]);
  const [releaseForm, setReleaseForm] = useState({ attempt_id: "", version: "", minimum_agent_version: "0.25.0", restart_policy: "never" as "never" | "on-failure" });

  const selected = useMemo(() => submissions.find((submission) => submission.id === selectedID), [selectedID, submissions]);
  const summary = useMemo(() => ({
    pending: submissions.filter((submission) => submission.state === "pending").length,
    approved: submissions.filter((submission) => submission.state === "approved").length,
    published: submissions.filter((submission) => submission.state === "published").length,
  }), [submissions]);
  const reviewReady = review.release_approved
    && (selected?.automatic_checks?.length ?? 0) >= 3
    && (selected?.automatic_checks?.every((check) => check.passed) ?? false);
  const selectedDevelopmentAttempt = useMemo(() => developmentAttempts.find((attempt) => attempt.attempt_id === selectedDevelopmentAttemptID)
    ?? developmentAttempts.find((attempt) => attempt.revision_id === selectedDevelopmentRevisionID),
  [developmentAttempts, selectedDevelopmentAttemptID, selectedDevelopmentRevisionID]);
  const selectedDevelopmentLogAttemptID = selectedDevelopmentAttempt?.attempt_id ?? "";
  const selectedDevelopmentLogIsServerUpload = selectedDevelopmentAttempt?.step === "server_upload";
  const selectedDeviceTransfer = developmentTransferStats(selectedDevelopmentAttempt?.analysis);
  const selectedSourceTotalFiles = selectedDevelopmentAttempt?.source_total_files ?? 0;
  const selectedSourceTotalBytes = selectedDevelopmentAttempt?.source_total_bytes ?? 0;
  const selectedSourceReusedFiles = selectedDevelopmentAttempt?.source_reused_files ?? 0;
  const selectedSourceReusedBytes = selectedDevelopmentAttempt?.source_reused_bytes ?? 0;
  const selectedSourceUploadedFiles = selectedDevelopmentAttempt?.source_uploaded_files ?? 0;
  const selectedSourceUploadedBytes = selectedDevelopmentAttempt?.source_uploaded_bytes ?? 0;
  const selectedSourceRequiredFiles = selectedDevelopmentAttempt?.source_stats_known ? Math.max(0, selectedSourceTotalFiles - selectedSourceReusedFiles) : 0;
  const selectedSourceRequiredBytes = selectedDevelopmentAttempt?.source_stats_known ? Math.max(0, selectedSourceTotalBytes - selectedSourceReusedBytes) : 0;
  const selectedNetworkFiles = (selectedDevelopmentAttempt?.source_uploaded_files ?? 0) + (selectedDeviceTransfer?.transferred_files ?? 0);
  const selectedNetworkBytes = (selectedDevelopmentAttempt?.source_uploaded_bytes ?? 0) + (selectedDeviceTransfer?.transferred_bytes ?? 0);
  const selectedNetworkRequiredFiles = selectedSourceRequiredFiles + (selectedDeviceTransfer ? Math.max(0, selectedDeviceTransfer.total_files - selectedDeviceTransfer.reused_files) : 0);
  const selectedNetworkRequiredBytes = selectedSourceRequiredBytes + (selectedDeviceTransfer ? Math.max(0, selectedDeviceTransfer.total_bytes - selectedDeviceTransfer.reused_bytes) : 0);

  const fail = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof APIError && reason.status === 401) {
      onUnauthorized();
      return;
    }
    onError(fallback);
  }, [onError, onUnauthorized]);

  const reload = useCallback(async () => {
    const [submissionResult, publisherResult, accessResult, attemptResult, candidateResult] = await Promise.all([
      getExtensionSubmissions(), getExtensionPublishers(), getExtensionDevelopmentAccesses().catch(() => []), getExtensionDevelopmentAttempts().catch(() => []), getExtensionDevelopmentDeviceCandidates(),
    ]);
    setSubmissions(submissionResult);
    setPublishers(publisherResult.filter((publisher) => publisher.active));
    setDevelopmentAccesses(accessResult.filter((access) => access.publisher_active));
    setDevelopmentAttempts(attemptResult);
    setDevelopmentCandidates(candidateResult);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([getExtensionSubmissions(), getExtensionPublishers(), getExtensionDevelopmentAccesses().catch(() => []), getExtensionDevelopmentAttempts().catch(() => []), getExtensionDevelopmentDeviceCandidates()])
      .then(([submissionResult, publisherResult, accessResult, attemptResult, candidateResult]) => {
        if (!active) return;
        setSubmissions(submissionResult);
        setPublishers(publisherResult.filter((publisher) => publisher.active));
        setDevelopmentAccesses(accessResult.filter((access) => access.publisher_active));
        setDevelopmentAttempts(attemptResult);
        setDevelopmentCandidates(candidateResult);
      })
      .catch((reason: unknown) => { if (active) fail(reason, "Die Extension-Prüfungen konnten nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fail]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      getExtensionDevelopmentAttempts().then(setDevelopmentAttempts).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedDevelopmentLogAttemptID || selectedDevelopmentLogIsServerUpload) {
      return undefined;
    }
    let active = true;
    let cursor = 0;
    let timer = 0;
    const decoders = { stdout: new TextDecoder(), stderr: new TextDecoder() };
    const loadOutput = async () => {
      try {
        let more = true;
        while (active && more) {
          const output = await getExtensionDevelopmentOutput(selectedDevelopmentLogAttemptID, cursor);
          if (!active) return;
          const entries = output.chunks.map((chunk) => {
            const bytes = decodeDevelopmentLogBytes(chunk.data_base64);
            return { cursor: chunk.cursor, stream: chunk.stream, text: bytes ? decoders[chunk.stream].decode(bytes, { stream: true }) : "[Logzeile konnte nicht dekodiert werden]\n" };
          });
          if (entries.length > 0) {
            cursor = output.next_cursor;
            setDevelopmentLog((current) => [...current, ...entries].slice(-2000));
          }
          more = output.truncated;
        }
        if (!active) return;
        setDevelopmentLogLoading(false);
        setDevelopmentLogError("");
        timer = window.setTimeout(loadOutput, 1000);
      } catch (reason) {
        if (!active) return;
        setDevelopmentLogLoading(false);
        if (reason instanceof APIError && reason.status === 401) {
          onUnauthorized();
          return;
        }
        setDevelopmentLogError("Der Logstream konnte gerade nicht geladen werden. RPControl versucht es erneut.");
        timer = window.setTimeout(loadOutput, 3000);
      }
    };
    void loadOutput();
    return () => { active = false; window.clearTimeout(timer); };
  }, [onUnauthorized, selectedDevelopmentLogAttemptID, selectedDevelopmentLogIsServerUpload]);

  async function selectDevelopmentDevice(publisher: ExtensionPublisher, deviceID: string) {
    const access = developmentAccesses.find((candidate) => candidate.publisher_id === publisher.id);
    if (!deviceID || deviceID === access?.device?.device_id) return;
    setBusy(`development-device:${publisher.id}`); onError(""); onNotice(""); setSandboxCredentials(null);
    try {
      const result = await setExtensionDevelopmentDevice(publisher.id, deviceID);
      setSandboxCredentials(result.credentials ?? null);
      await reload();
      onNotice(`Der Test-Pi ${result.device.serial_number || result.device.device_cn} ist jetzt ausschließlich ${publisher.name} zugeordnet.`);
    } catch (reason) { fail(reason, "Das Entwicklungsgerät muss aktiv, konfiguriert, frei von Kundenzuordnungen und Kundenmessdaten und mindestens auf Agent 0.25.0 sein."); }
    finally { setBusy(""); }
  }

  async function resetSandboxCredentials(publisherID: string) {
    setBusy(`sandbox-credentials:${publisherID}`); onError(""); onNotice("");
    try {
      const result = await resetExtensionDevelopmentSandboxCredentials(publisherID);
      setSandboxCredentials(result.credentials);
      onNotice("Das Kennwort für den Sandbox-Benutzer wurde neu erzeugt. Es wird nur jetzt angezeigt.");
    } catch (reason) { fail(reason, "Der Sandbox-Zugang konnte nicht zurückgesetzt werden."); }
    finally { setBusy(""); }
  }

  async function takeAsRelease(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!releaseForm.attempt_id) return;
    setBusy(`release:${releaseForm.attempt_id}`); onError(""); onNotice("");
    try {
      await requestExtensionDevelopmentRelease(releaseForm.attempt_id, releaseForm.version, releaseForm.minimum_agent_version, releaseForm.restart_policy);
      setReleaseForm({ attempt_id: "", version: "", minimum_agent_version: "0.25.0", restart_policy: "never" });
      await reload();
      onNotice("Der Test-Pi sammelt jetzt ausschließlich die Laufzeitdateien. Danach erscheint die Release-Einreichung zur Prüfung.");
    } catch (reason) { fail(reason, "Der startbestätigte Entwicklungsversuch konnte nicht als Release übernommen werden."); }
    finally { setBusy(""); }
  }

  function openDevelopmentLog(attempt: ExtensionDevelopmentAttempt) {
    setDevelopmentLog([]);
    setDevelopmentLogError("");
    setDevelopmentLogLoading(attempt.step !== "server_upload");
    setSelectedDevelopmentAttemptID(attempt.attempt_id);
    setSelectedDevelopmentRevisionID(attempt.revision_id);
  }

  function toggleDevelopmentDetails(attempt: ExtensionDevelopmentAttempt) {
    if (selectedDevelopmentAttempt?.attempt_id === attempt.attempt_id) closeDevelopmentLog();
    else openDevelopmentLog(attempt);
  }

  function closeDevelopmentLog() {
    setSelectedDevelopmentAttemptID("");
    setSelectedDevelopmentRevisionID("");
    setDevelopmentLog([]);
    setDevelopmentLogError("");
    setDevelopmentLogLoading(false);
  }

  async function deleteDevelopmentRevision(attempt: ExtensionDevelopmentAttempt) {
    if (!attempt.can_delete) {
      onError(developmentDeleteBlocker(attempt));
      return;
    }
    if (!window.confirm(`Revision ${attempt.revision_number} von „${attempt.name}“ vollständig löschen? Serverdateien, Build-/Release-Sammeldateien, Versuche, Logs und der zugehörige Stand auf dem Test-Pi werden entfernt. Andere Revisionen bleiben erhalten.`)) return;
    setBusy(`development-delete:${attempt.revision_id}`); onError(""); onNotice("");
    try {
      await deleteExtensionDevelopmentRevision(attempt.revision_id);
      closeDevelopmentLog();
      if (developmentAttempts.some((candidate) => candidate.revision_id === attempt.revision_id && candidate.attempt_id === releaseForm.attempt_id)) {
        setReleaseForm({ attempt_id: "", version: "", minimum_agent_version: "0.25.0", restart_policy: "never" });
      }
      await reload();
      onNotice(`Revision ${attempt.revision_number} wurde auf dem Server gelöscht. Die Bereinigung dieser Revision auf dem Test-Pi wurde beauftragt.`);
    } catch (reason) {
      fail(reason, reason instanceof APIError && reason.status === 409
        ? "Die Revision ist inzwischen aktiv, aktuell oder an ein Release gebunden und kann nicht gelöscht werden."
        : "Die Entwicklungsrevision konnte nicht gelöscht werden.");
    } finally { setBusy(""); }
  }

  async function cancelDevelopmentAttempt(attempt: ExtensionDevelopmentAttempt) {
    if (!cancellableDevelopmentStates.has(attempt.state)) return;
    if (!window.confirm(`Den laufenden Vorgang für Revision ${attempt.revision_number} wirklich abbrechen? Prozesse auf dem Test-Pi werden beendet.`)) return;
    setBusy(`development-cancel:${attempt.attempt_id}`); onError(""); onNotice("");
    try {
      const result = await cancelExtensionDevelopmentAttempt(attempt.attempt_id);
      await reload();
      onNotice(result.delivery === "published"
        ? `Der Abbruch für Revision ${attempt.revision_number} wurde an den Test-Pi gesendet.`
        : `Der Abbruch für Revision ${attempt.revision_number} ist gespeichert und wird dem Test-Pi automatisch zugestellt.`);
    } catch (reason) {
      fail(reason, reason instanceof APIError && reason.status === 409
        ? "Der Vorgang ist bereits beendet oder der Test-Pi benötigt mindestens Agent 0.16.0."
        : "Der Entwicklungsversuch konnte nicht abgebrochen werden.");
    } finally { setBusy(""); }
  }

  async function restartDevelopmentAttempt(attempt: ExtensionDevelopmentAttempt, mode: "restart" | "rebuild" = "restart") {
    if (!canRestartDevelopmentAttempt(attempt)) return;
    const action = mode === "rebuild" ? "neu bauen, einrichten und starten" : "aus den vorhandenen Laufzeitdateien ohne Build oder Installation neu starten";
    if (!window.confirm(`Programm der Revision ${attempt.revision_number} ${action}? Ein laufender Prozess wird zuvor beendet. Die Konsolenausgabe bleibt im selben Versuch erhalten.`)) return;
    setBusy(`development-restart:${attempt.attempt_id}`); onError(""); onNotice("");
    try {
      const result = await restartExtensionDevelopmentAttempt(attempt.attempt_id, mode);
      await reload();
      const requested = mode === "rebuild" ? "Die neue Einrichtung" : "Der Neustart";
      onNotice(result.delivery === "published"
        ? `${requested} der Revision ${attempt.revision_number} wurde an den Test-Pi gesendet. Die Konsolenausgabe des neuen Laufs erscheint im selben Versuch.`
        : `${requested} der Revision ${attempt.revision_number} ist gespeichert und wird dem Test-Pi automatisch zugestellt.`);
    } catch (reason) {
      fail(reason, reason instanceof APIError && reason.status === 409
        ? "Die Aktion benötigt Agent 0.32.0 oder neuer und einen freien, verwendbaren Test-Pi. Ein laufender Build oder ein releasegebundener Versuch kann nicht ersetzt werden."
        : "Der Entwicklungsversuch konnte nicht neu gestartet werden.");
    } finally { setBusy(""); }
  }

  async function purgeDevelopmentDevice(attempt: ExtensionDevelopmentAttempt) {
    if (!attempt.device_id) return;
    const label = attempt.serial_number || attempt.device_cn;
    if (!window.confirm(`Alle nicht freigegebenen Entwicklungsrevisionen von ${label} löschen? Die Revisionen samt Versuchen, Logs und Dateien werden aus RPControl entfernt und die Entwicklungsdaten auf dem Pi bereinigt. An Releases gebundene Revisionen, installierte Releases, Messdaten und Agent-Updates bleiben erhalten.`)) return;
    setBusy(`development-purge:${attempt.device_id}`); onError(""); onNotice("");
    try {
      const result = await purgeExtensionDevelopmentDevice(attempt.device_id);
      closeDevelopmentLog();
      await reload();
      onNotice(`${result.deleted_revisions} nicht freigegebene Revisionen von ${label} wurden gelöscht. Release-gebunden und erhalten: ${result.protected_revisions}. Die Pi-Bereinigung wurde gesendet (Audit ${result.audit_id}).`);
    } catch (reason) {
      fail(reason, reason instanceof APIError && reason.status === 409
        ? "Das Löschen aller Entwicklungsrevisionen benötigt Agent 0.28.0 oder neuer."
        : "Die Entwicklungsrevisionen und Pi-Daten konnten nicht vollständig gelöscht werden.");
    } finally { setBusy(""); }
  }

  function openSubmission(submission: ExtensionSubmission) {
    setSelectedID((current) => current === submission.id ? "" : submission.id);
    setRejectionReason(submission.state === "rejected" ? submission.review_notes ?? "" : "");
    setReview({
      ...initialReview,
      review_notes: submission.state === "rejected" ? "" : submission.review_notes ?? "",
    });
  }

  async function approve(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    setBusy(`approve:${selected.id}`); onError(""); onNotice("");
    try {
      const result = await approveExtensionSubmission(selected.id, review);
      await reload();
      setReview(initialReview);
      onNotice(result.state === "published"
        ? `„${selected.name}“ ${selected.version} wurde freigegeben und veröffentlicht.`
        : `„${selected.name}“ wurde freigegeben. Die automatische Veröffentlichung ist noch ausständig.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.code === "extension_dataset_version_conflict") onError(reason.message);
      else fail(reason, "Die Einreichung konnte nicht freigegeben werden. Prüfe Version, Artefakt und Publisher-Zuordnung.");
    }
    finally { setBusy(""); }
  }

  async function reject() {
    if (!selected || rejectionReason.trim().length < 3 || !window.confirm(`Einreichung „${selected.name}“ wirklich ablehnen?`)) return;
    setBusy(`reject:${selected.id}`); onError(""); onNotice("");
    try {
      await rejectExtensionSubmission(selected.id, rejectionReason.trim());
      await reload();
      setSelectedID("");
      onNotice("Die Einreichung wurde abgelehnt und bleibt im Audit-Verlauf erhalten.");
    } catch (reason) { fail(reason, "Die Einreichung konnte nicht abgelehnt werden."); }
    finally { setBusy(""); }
  }

  async function sign() {
    if (!selected || !window.confirm(`Freigegebene Extension „${selected.name}“ ${selected.version} jetzt signieren und veröffentlichen?`)) return;
    setBusy(`sign:${selected.id}`); onError(""); onNotice("");
    try {
      await signExtensionSubmission(selected.id);
      await reload();
      onNotice(`„${selected.name}“ ${selected.version} ist veröffentlicht und kann Geräten angeboten werden.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.code === "extension_dataset_version_conflict") {
        onError(reason.message);
      } else if (reason instanceof APIError && reason.status === 409) {
        onError("Der freigegebene Releasevertrag ist unvollständig oder wurde seit der Freigabe geändert. Bitte nicht erneut freigeben, sondern den Vertragsfehler prüfen.");
      } else {
        fail(reason, "Der getrennte Signierdienst ist momentan nicht erreichbar. Die Freigabe bleibt erhalten und kann erneut signiert werden.");
      }
    }
    finally { setBusy(""); }
  }

  async function remove() {
    if (!selected || (selected.state !== "published" && selected.state !== "rejected") || !window.confirm(
      `Extension-Version „${selected.name}“ ${selected.version} endgültig löschen? Paket, Diagnosedaten und Einreichung werden entfernt. Ein minimaler Audit-Nachweis bleibt erhalten.`,
    )) return;
    setBusy(`delete:${selected.id}`); onError(""); onNotice("");
    try {
      const result = await deleteExtensionSubmission(selected.id);
      await reload();
      setSubmissions((current) => current.filter((submission) => submission.id !== selected.id));
      setSelectedID("");
      onNotice(selected.state === "published"
        ? result.catalog_removed
          ? `„${selected.name}“ ${selected.version} wurde vollständig gelöscht, von den Geräten entfernt und aus dem Katalog genommen.`
          : `„${selected.name}“ ${selected.version} wurde vollständig gelöscht und von den Geräten entfernt.`
        : `„${selected.name}“ ${selected.version} wurde gelöscht.`);
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) {
        onError("Diese Extension ist noch auf mindestens einem Gerät aktiviert und kann erst nach dem Ausschalten gelöscht werden.");
      } else {
        fail(reason, "Die Extension-Version konnte nicht gelöscht werden.");
      }
    } finally { setBusy(""); }
  }

  async function createPublisher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("publisher:create"); onError(""); onNotice(""); setIssuedToken("");
    try {
      const result = await createExtensionPublisher(publisherForm.name.trim(), publisherForm.signing_public_key.trim());
      setIssuedToken(result.api_token);
      setPublisherForm({ name: "", signing_public_key: "" });
      await reload();
    } catch (reason) { fail(reason, "Der Entwicklerzugang konnte nicht angelegt werden. Name und Ed25519-Schlüssel müssen eindeutig und gültig sein."); }
    finally { setBusy(""); }
  }

  async function deactivatePublisher(publisher: ExtensionPublisher) {
    if (!window.confirm(`Entwicklerzugang „${publisher.name}“ deaktivieren und aus der aktiven Liste entfernen? Token, Sandbox-Login und laufende Entwicklung werden gesperrt; historische Einreichungen und Auditnachweise bleiben erhalten.`)) return;
    setBusy(`publisher:${publisher.id}`); onError(""); onNotice(""); setSandboxCredentials(null);
    try {
      await deactivateExtensionPublisher(publisher.id);
      await reload();
      onNotice(`Der Entwicklerzugang „${publisher.name}“ wurde deaktiviert und aus der aktiven Liste entfernt.`);
    } catch (reason) { fail(reason, "Der Entwicklerzugang konnte nicht deaktiviert werden."); }
    finally { setBusy(""); }
  }

  function renderSelectedSubmissionDetails() {
    if (!selected) return null;
    return <section className="extension-review-card" aria-label={`Extension-Prüfung für ${selected.name}`}>
      <div className="panel-heading extension-review-heading"><div><span className="device-detail-kicker">Extension-Einreichung</span><h2>{selected.name} · {selected.version}</h2><p>{selected.extension_id} · eingereicht von {selected.publisher_name}</p></div></div>
      <div className="extension-review-meta">
        <span><b>Beschreibung</b>{selected.description}</span>
        <span><b>Mindest-Agent</b>{selected.minimum_agent_version}</span>
        <span><b>Paket und Start</b>{selected.artifact_kind === "files" ? "Dateibasierter Build" : selected.artifact_kind === "bundle" ? "Mehrdateien-Paket" : "Einzeldatei"} · {selected.runtime ?? "exec"} · <code>{selected.entrypoint ?? "extension-linux-arm64"}</code></span>
        {selected.artifact_kind !== "executable" && <span><b>Bei Absturz</b>{selected.restart_policy === "never" ? "Beendet lassen" : "Nach 10 Sekunden neu starten"}</span>}
        <span><b>Startargumente</b>{selected.arguments?.length ? selected.arguments.map((argument) => JSON.stringify(argument)).join(" ") : "keine"}</span>
        <span><b>Artefakt</b>{readableSize(selected.artifact_size)} · <code>{selected.artifact_sha256}</code></span>
        <a className="text-action" href={extensionSubmissionArtifactURL(selected.id)}>Prüfpaket herunterladen</a>
      </div>

      {selected.state === "pending" && <form className="management-form extension-review-form" onSubmit={approve}>
        {!selected.publisher_active && <p className="review-warning">Der Entwicklerzugang ist deaktiviert. Diese Einreichung kann nicht freigegeben werden.</p>}
        <section className="automatic-review" aria-label="Automatisch geprüft"><h3>Automatisch geprüft</h3>
          <p className="automatic-review-reference">Publisher <code>{selected.publisher_key_sha256?.slice(0, 8)}</code> · Quelle <code>sha256:{selected.artifact_sha256.slice(0, 12)}…</code></p>
          <ul>{selected.automatic_checks?.map((check) => <li className={check.passed ? "" : "failed"} key={check.code}><span aria-hidden="true">{check.passed ? "✓" : "!"}</span><div><strong>{check.label}</strong><small>{check.detail}</small></div></li>)}</ul>
        </section>
        <section className="execution-model" aria-label="Ausführungsmodell"><h3>Ausführungsmodell</h3><p>Extensions laufen auf dem Raspberry Pi als root mit vollständigem Geräte-, Netzwerk- und Dateisystemzugriff. Es gibt keinen separaten Rechtevertrag.</p></section>
        <div className="review-evidence-grid"><label>Prüfnotiz · optional<textarea maxLength={2000} rows={3} value={review.review_notes} onChange={(event) => setReview({ ...review, review_notes: event.target.value })} /></label></div>
        <fieldset className="review-checklist"><legend>Freigabe</legend>
          <label><input required type="checkbox" checked={review.release_approved} onChange={(event) => setReview({ ...review, release_approved: event.target.checked })} />Ich habe Paket, Prüfergebnisse und die vollständige root-Ausführung geprüft und veröffentliche diese Version.</label>
        </fieldset>
        <div className="review-actions"><button className="primary-button compact-primary" disabled={!selected.publisher_active || !reviewReady || busy === `approve:${selected.id}`}>Freigeben und veröffentlichen</button><div><input aria-label="Ablehnungsgrund" maxLength={1000} placeholder="Ablehnungsgrund" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} /><button className="danger-link" type="button" disabled={rejectionReason.trim().length < 3 || busy === `reject:${selected.id}`} onClick={() => void reject()}>Ablehnen</button></div></div>
      </form>}

      {selected.state === "approved" && <div className="extension-signing-step">
        <div><h3>Veröffentlichung ausständig</h3><p>Die Release-Prüfung ist abgeschlossen, aber Signierung oder Dataset-Aktivierung konnten die Veröffentlichung noch nicht abschließen. Der private Schlüssel bleibt ausschließlich beim getrennten RPControl-Signierdienst.</p></div>
        <div className="review-actions"><button className="primary-button compact-primary" type="button" disabled={busy === `sign:${selected.id}`} onClick={() => void sign()}>Veröffentlichung erneut anstoßen</button><div><input aria-label="Ablehnungsgrund" maxLength={1000} placeholder="Prüfung zurückziehen mit Grund" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} /><button className="danger-link" type="button" disabled={rejectionReason.trim().length < 3 || busy === `reject:${selected.id}`} onClick={() => void reject()}>Ablehnen</button></div></div>
      </div>}

      {selected.state === "published" && <div className="extension-result"><strong>Veröffentlicht · Sequenz {selected.release_sequence}</strong><code>/downloads/extensions/{selected.extension_id}/{selected.version}/</code><p>Geräte sehen diese Version über den signierten Katalog. Die veröffentlichten Bytes können nicht überschrieben werden.</p><button className="danger-link" type="button" disabled={busy === `delete:${selected.id}`} onClick={() => void remove()}>Version löschen</button></div>}
      {selected.state === "rejected" && <div className="extension-result rejected"><strong>Abgelehnt</strong><p>{selected.review_notes}</p><button className="danger-link" type="button" disabled={busy === `delete:${selected.id}`} onClick={() => void remove()}>Einreichung löschen</button></div>}
    </section>;
  }

  return <>
    <section className="management-summary extensions" aria-label="Extension-Status">
      <article><span>Offene Prüfungen</span><strong>{summary.pending}</strong><small>noch nicht freigegeben</small></article>
      <article><span>Veröffentlichung ausständig</span><strong>{summary.approved}</strong><small>fachlich geprüft</small></article>
      <article><span>Veröffentlicht</span><strong>{summary.published}</strong><small>im Gerätekatalog</small></article>
    </section>

    <section className="device-panel extension-development-panel">
      <div className="panel-heading device-panel-heading"><div><h2>Entwicklungsversuche</h2><p>Alle Projektstände, Testläufe und Konsolenausgaben. Die Liste bleibt bei vielen Einträgen innerhalb dieses Bereichs scrollbar.</p></div></div>
      <div className="device-table-wrap development-attempts-scroll"><table className="management-table"><thead><tr><th>Projektstand</th><th>Entwickler</th><th>Schritt</th><th>Status</th><th></th></tr></thead><tbody>
        {developmentAttempts.map((attempt) => {
          const expanded = selectedDevelopmentAttempt?.attempt_id === attempt.attempt_id;
          const detailID = `development-details-${attempt.attempt_id}`;
          return <Fragment key={attempt.attempt_id}>
            <tr className={expanded ? "device-row selected" : "device-row"}><td><strong>{attempt.name}</strong><small>{attempt.extension_id} · Revision {attempt.revision_number}</small></td><td>{attempt.publisher_name}</td><td>{developmentStepLabel(attempt.step)}</td><td><span className={`state-badge extension-${attempt.state === "failed" ? "rejected" : attempt.state === "succeeded" || attempt.state === "release_ready" ? "published" : "pending"}`}><i />{developmentStatusLabel(attempt)}</span>{attempt.error_summary && <small>{attempt.error_summary}</small>}</td><td><div className="table-actions">
              <button className="text-action" type="button" aria-expanded={expanded} aria-controls={detailID} onClick={() => toggleDevelopmentDetails(attempt)}>{expanded ? "Schließen" : "Details"}</button>
            </div></td></tr>
            {expanded && <tr className="device-detail-row"><td colSpan={5}><section id={detailID} className="device-details-inline" aria-label={`Entwicklungsdetails für Revision ${attempt.revision_number}`}>
              <section className="device-detail-activity">
                <div className="development-log-heading"><div><span className="device-detail-kicker">Entwicklungsversuch</span><h3>{attempt.name} · Revision {attempt.revision_number}</h3><p>{attempt.extension_id} · {developmentStatusLabel(attempt)} · Schritt: {developmentStepLabel(attempt.step)} · Rohlogs werden 30 Tage aufbewahrt.</p></div><div className="table-actions">
                  {canRestartDevelopmentAttempt(attempt) && <button className="text-action" type="button" disabled={busy === `development-restart:${attempt.attempt_id}`} onClick={() => void restartDevelopmentAttempt(attempt)}>Programm neu starten</button>}
                  {canRestartDevelopmentAttempt(attempt) && <button className="text-action" type="button" disabled={busy === `development-restart:${attempt.attempt_id}`} onClick={() => void restartDevelopmentAttempt(attempt, "rebuild")}>Neu bauen und einrichten</button>}
                  {attempt.error_code === "runtime_preparation_required" && <span className="device-detail-note">Die Laufzeitdateien fehlen, wurden verändert oder gehören zu einer abgelösten Einrichtung. Bitte „Neu bauen und einrichten“ verwenden.</span>}
                  {!canRestartDevelopmentAttempt(attempt) && cancellableDevelopmentStates.has(attempt.state) && <button className="text-action" type="button" disabled title="Ein Neustart ist erst nach dem Programmende oder ab dem bestätigten Start möglich. Ein laufender Build oder Release-Upload muss zuerst abgebrochen werden.">Programm neu starten</button>}
                  {cancellableDevelopmentStates.has(attempt.state) && <button className="danger-link" type="button" disabled={busy === `development-cancel:${attempt.attempt_id}`} onClick={() => void cancelDevelopmentAttempt(attempt)}>Vorgang abbrechen</button>}
                  {attempt.state === "cancelling" && <button className="danger-link" type="button" disabled>Wird abgebrochen …</button>}
                  {canTakeDevelopmentRelease(attempt) && releaseForm.attempt_id !== attempt.attempt_id && <button className="text-action" type="button" onClick={() => setReleaseForm({ ...releaseForm, attempt_id: attempt.attempt_id, version: attempt.release_version || releaseForm.version, minimum_agent_version: attempt.minimum_agent_version || releaseForm.minimum_agent_version, restart_policy: attempt.restart_policy || releaseForm.restart_policy })}>{attempt.action === "release" ? "Release erneut versuchen" : "Als Release übernehmen"}</button>}
                  {attempt.submission_id && <button className="text-action" type="button" onClick={() => setSelectedID(attempt.submission_id ?? "")}>Release-Prüfung öffnen</button>}
                  {attempt.device_id && <button className="danger-link" type="button" disabled={busy === `development-purge:${attempt.device_id}`} onClick={() => void purgeDevelopmentDevice(attempt)}>Alle Revisionen dieses Pis löschen</button>}
                  <button className="danger-link" type="button" disabled={!attempt.can_delete || busy === `development-delete:${attempt.revision_id}`} title={attempt.can_delete ? "Revision samt Entwicklungsversuchen und Logs löschen" : developmentDeleteBlocker(attempt)} onClick={() => void deleteDevelopmentRevision(attempt)}>Revision löschen</button>
                </div></div>
                <div className="development-transfer-grid" aria-label="Hash-Übertragungsstatistik">
                  <article><span>Projektinhalt</span><strong>{selectedSourceTotalFiles.toLocaleString(displayLocale)} Hash-Dateien · {readableSize(selectedSourceTotalBytes)}</strong><small>Eindeutige SHA-256-Inhalte dieser Revision</small></article>
                  <article><span>Entwickler → Server</span>{attempt.source_stats_known ? <><strong>{selectedSourceUploadedFiles.toLocaleString(displayLocale)} / {selectedSourceRequiredFiles.toLocaleString(displayLocale)} neu · {readableSize(selectedSourceUploadedBytes)} / {readableSize(selectedSourceRequiredBytes)}</strong><progress max={Math.max(1, selectedSourceRequiredBytes)} value={selectedSourceRequiredBytes === 0 ? 1 : selectedSourceUploadedBytes} /><small>{selectedSourceReusedFiles.toLocaleString(displayLocale)} Dateien · {readableSize(selectedSourceReusedBytes)} bereits per Hash vorhanden</small></> : <><strong>Noch nicht erfasst</strong><small>Diese Revision entstand vor der Übertragungsstatistik.</small></>}</article>
                  <article><span>Server → Test-Pi</span>{selectedDeviceTransfer ? <><strong>{(selectedDeviceTransfer.reused_files + selectedDeviceTransfer.transferred_files).toLocaleString(displayLocale)} / {selectedDeviceTransfer.total_files.toLocaleString(displayLocale)} vorhanden · {readableSize(selectedDeviceTransfer.reused_bytes + selectedDeviceTransfer.transferred_bytes)} / {readableSize(selectedDeviceTransfer.total_bytes)}</strong><progress max={Math.max(1, selectedDeviceTransfer.total_bytes)} value={selectedDeviceTransfer.reused_bytes + selectedDeviceTransfer.transferred_bytes} /><small>{selectedDeviceTransfer.reused_files.toLocaleString(displayLocale)} Dateien · {readableSize(selectedDeviceTransfer.reused_bytes)} aus lokalem Hashspeicher wiederverwendet</small></> : <><strong>Noch nicht gemeldet</strong><small>Verfügbar, sobald ein Agent ab 0.15.0 den Download übernimmt.</small></>}</article>
                  <article><span>Netzwerk dieser Revision</span><strong>{selectedNetworkFiles.toLocaleString(displayLocale)} / {selectedNetworkRequiredFiles.toLocaleString(displayLocale)} Dateien · {readableSize(selectedNetworkBytes)} / {readableSize(selectedNetworkRequiredBytes)}</strong><small>Summe der erfassten Strecken; Hash-Treffer zählen nicht als Übertragung.</small></article>
                </div>
                {!attempt.can_delete && <p className="development-delete-blocker">{developmentDeleteBlocker(attempt)}</p>}
                {attempt.step === "server_upload"
                  ? <p className="development-log-empty">Der Projektstand liegt auf dem Server. Sobald der Test-Pi den Versuch übernimmt, erscheint sein Log hier automatisch.</p>
                  : <pre className="development-log-output" aria-live="polite">{developmentLog.map((entry) => <span className={entry.stream === "stderr" ? "stderr" : undefined} key={entry.cursor}>{entry.text}</span>)}{developmentLogLoading && developmentLog.length === 0 && <span>Log wird geladen …</span>}{!developmentLogLoading && developmentLog.length === 0 && !developmentLogError && <span>Noch keine Konsolenausgabe empfangen.</span>}</pre>}
                {developmentLogError && <p className="development-log-error">{developmentLogError}</p>}
              </section>
            </section></td></tr>}
          </Fragment>;
        })}
        {developmentAttempts.length === 0 && <tr><td colSpan={5} className="table-empty">Noch keine Entwicklungsversuche.</td></tr>}
      </tbody></table></div>
      {releaseForm.attempt_id && <form className="management-form compact-form" onSubmit={takeAsRelease}><p className="device-detail-note">Die getesteten Laufzeitdateien werden in die Einreichung übernommen. Test und späteres Release laufen identisch als root; ein separater Rechtevertrag entfällt.</p><label>Release-Version<input required pattern="[0-9]+\.[0-9]+\.[0-9]+" value={releaseForm.version} onChange={(event) => setReleaseForm({ ...releaseForm, version: event.target.value })} /></label><label>Mindest-Agent<input required pattern="[0-9]+\.[0-9]+\.[0-9]+" value={releaseForm.minimum_agent_version} onChange={(event) => setReleaseForm({ ...releaseForm, minimum_agent_version: event.target.value })} /></label><label>Bei Absturz<select value={releaseForm.restart_policy} onChange={(event) => setReleaseForm({ ...releaseForm, restart_policy: event.target.value as "never" | "on-failure" })}><option value="never">Beendet lassen</option><option value="on-failure">Neu starten</option></select></label><button className="primary-button compact-primary" disabled={busy === `release:${releaseForm.attempt_id}`}>Laufzeitdateien einsammeln</button><button className="text-action" type="button" onClick={() => setReleaseForm({ ...releaseForm, attempt_id: "" })}>Abbrechen</button></form>}
    </section>

    <section className="device-panel extension-submissions-panel">
      <div className="panel-heading device-panel-heading"><div><h2>Extension-Einreichungen</h2><p>Nur veröffentlichte, RPControl-signierte Versionen erscheinen bei Geräten.</p></div><button className="text-action" type="button" onClick={() => void reload()}>Aktualisieren</button></div>
      <div className="device-table-wrap"><table className="management-table"><thead><tr><th>Extension</th><th>Entwickler</th><th>Eingereicht</th><th>Status</th><th></th></tr></thead><tbody>
        {submissions.slice(0, visibleCount).map((submission) => {
          const expanded = selectedID === submission.id;
          const detailID = `submission-details-${submission.id}`;
          return <Fragment key={submission.id}>
            <tr className={expanded ? "device-row selected" : "device-row"}><td><strong>{submission.name}</strong><small>{submission.extension_id} · {submission.version}</small><small>{submission.artifact_kind === "files" ? "Dateien gesamt" : "Paketgröße"}: {submission.release_size !== undefined ? readableSize(submission.release_size) : submission.artifact_kind === "files" ? "Noch nicht verfügbar" : readableSize(submission.artifact_size)}</small></td><td>{submission.publisher_name}</td><td>{new Date(submission.submitted_at).toLocaleDateString(displayLocale)}</td><td><span className={`state-badge extension-${submission.state}`}><i />{stateLabels[submission.state]}</span></td><td><div className="table-actions"><button className="text-action" type="button" aria-expanded={expanded} aria-controls={detailID} onClick={() => openSubmission(submission)}>{expanded ? "Schließen" : submission.state === "pending" ? "Prüfen" : "Details"}</button></div></td></tr>
            {expanded && <tr className="device-detail-row"><td colSpan={5}><section id={detailID} className="device-details-inline">{renderSelectedSubmissionDetails()}</section></td></tr>}
          </Fragment>;
        })}
        {!loading && submissions.length === 0 && <tr><td colSpan={5} className="table-empty">Keine Einreichungen vorhanden.</td></tr>}
        {loading && <tr><td colSpan={5} className="table-empty table-loading">Einreichungen werden geladen …</td></tr>}
      </tbody></table></div>
      {submissions.length > 5 && <div className="list-disclosure">{visibleCount < submissions.length ? <button className="disclosure-button" type="button" onClick={() => setVisibleCount(submissions.length)}>Weitere anzeigen ({submissions.length - visibleCount})</button> : <button className="disclosure-button" type="button" onClick={() => setVisibleCount(5)}>Weniger anzeigen</button>}</div>}
    </section>

    <details className="device-panel extension-publishers-panel">
      <summary>Entwicklerzugänge ({publishers.length} aktiv)</summary>
      <div className="extension-publishers-content">
        <div className="archive-toolbar"><p>Token erlauben nur signierte Einreichungen in den Prüfbereich, niemals eine Veröffentlichung.</p><button className="text-action" type="button" aria-expanded={showPublisherForm} aria-controls="publisher-create-form" onClick={() => { setShowPublisherForm((value) => !value); setIssuedToken(""); }}>{showPublisherForm ? "Schließen" : "Neuer Zugang"}</button></div>
        <div className="table-actions publisher-downloads"><a className="text-action" href="/downloads/extension-publisher/extension-developer-guide.md">Anleitung & API</a><a className="text-action" href="/downloads/extension-publisher/extension-measurement-ipc-v1.md">Messdaten-IPC</a><a className="text-action" href="/downloads/extension-publisher/rpcontrol-publisher-windows-amd64.exe">Publisher für Windows</a><a className="text-action" href="/downloads/extension-publisher/rpcontrol-publisher-linux-amd64">Publisher für Linux</a></div>
        {showPublisherForm && <form id="publisher-create-form" className="management-form compact-form publisher-form" onSubmit={createPublisher}><label>Name<input required maxLength={120} value={publisherForm.name} onChange={(event) => setPublisherForm({ ...publisherForm, name: event.target.value })} /></label><label>Ed25519 Public Key · Raw Base64<input required value={publisherForm.signing_public_key} onChange={(event) => setPublisherForm({ ...publisherForm, signing_public_key: event.target.value.trim() })} /></label><button className="primary-button compact-primary" disabled={busy === "publisher:create"}>Zugang anlegen</button></form>}
        {issuedToken && <div className="publisher-token" role="status"><strong>API-Token jetzt sicher übergeben</strong><textarea readOnly rows={2} value={issuedToken} /><p>Dieser Token wird nur einmal angezeigt. RPControl speichert ausschließlich seinen SHA-256-Wert.</p></div>}
        <div className="device-table-wrap"><table className="management-table publisher-access-table"><thead><tr><th>Entwickler</th><th>Test-Pi</th><th>Sandbox-Kundenportal</th><th>Kennwort</th><th></th></tr></thead><tbody>
          {publishers.map((publisher) => {
            const access = developmentAccesses.find((candidate) => candidate.publisher_id === publisher.id);
            const currentDeviceID = access?.device?.device_id ?? "";
            return <tr key={publisher.id}>
              <td><strong>{publisher.name}</strong><small>seit {new Date(publisher.created_at).toLocaleDateString(displayLocale)}</small></td>
              <td className="publisher-device-cell"><select aria-label={`Test-Pi für ${publisher.name}`} value={currentDeviceID} disabled={!access || busy === `development-device:${publisher.id}`} onChange={(event) => void selectDevelopmentDevice(publisher, event.target.value)}>
                <option value="" disabled>Test-Pi auswählen</option>
                {access?.device && !developmentCandidates.some((candidate) => candidate.device_id === currentDeviceID) && <option value={currentDeviceID}>{access.device.serial_number || access.device.device_cn} - aktuell</option>}
                {developmentCandidates.map((candidate) => {
                  const current = candidate.device_id === currentDeviceID;
                  const assignedElsewhere = Boolean(candidate.assigned_publisher_id && candidate.assigned_publisher_id !== publisher.id);
                  const disabled = !current && (!candidate.eligible || assignedElsewhere);
                  const detail = current ? "aktuell" : assignedElsewhere ? `bereits ${candidate.assigned_publisher_name} zugeordnet` : candidate.eligible ? "bereit" : `nicht wählbar: ${developmentCandidateReason(candidate)}`;
                  return <option disabled={disabled} key={candidate.device_id} value={candidate.device_id}>{candidate.serial_number || candidate.device_cn} - {detail}</option>;
                })}
              </select></td>
              <td className="publisher-sandbox-cell">{access?.sandbox.initialized ? <><strong>{access.sandbox.login_identifier}</strong><small>{access.sandbox.measurement_point_name} · Generation {access.sandbox.generation}</small><form method="post" target="_blank" action={`/api/admin/extension-publishers/${publisher.id}/development-sandbox/login`}><button className="text-action" type="submit">Sandbox öffnen ↗</button></form></> : <><span>Noch nicht angelegt</span><small>Entsteht mit der ersten Test-Pi-Zuordnung</small></>}</td>
              <td className="publisher-password-cell">{access?.sandbox.initialized ? <button className="text-action" type="button" disabled={busy === `sandbox-credentials:${publisher.id}`} onClick={() => void resetSandboxCredentials(publisher.id)}>Kennwort neu erzeugen</button> : <small>Nach Pi-Zuordnung verfügbar</small>}</td>
              <td><div className="table-actions publisher-access-actions"><button className="danger-link" type="button" disabled={busy === `publisher:${publisher.id}`} onClick={() => void deactivatePublisher(publisher)}>Deaktivieren</button></div></td>
            </tr>;
          })}
          {publishers.length === 0 && <tr><td colSpan={5} className="table-empty">Noch keine aktiven Entwicklerzugänge vorhanden.</td></tr>}
        </tbody></table></div>
        {sandboxCredentials && <div className="publisher-token sandbox-password-token" role="status"><strong>Sandbox-Zugang jetzt sicher speichern</strong><p>Login: <code>{sandboxCredentials.login_identifier}</code></p><textarea readOnly rows={2} value={sandboxCredentials.password} /><p>Das Kennwort wird nur einmal angezeigt. Die Datenbank speichert ausschließlich den Argon2id-Hash.</p></div>}
      </div>
    </details>
  </>;
}
